import axios from "@/utils/axios";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/react-query";

// Interfaces para los tipos de respuesta del API
export interface TemplateVariable {
  id: string;
  display_name: string;
  mapping_name: string;
  type?: string; // Agregar el tipo de variable del API
  document_generator_template_id: string;
  created_at: string;
  updated_at: string;
}

export interface GeneratedDocument {
  id: string;
  // Añadir más campos según sea necesario
}

export interface Media {
  id: string;
  // Añadir más campos según sea necesario
}

export interface DocumentTemplate {
  id: string;
  display_name: string;
  mapping_name: string;
  description: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  variables?: TemplateVariable[];
  generated_documents?: GeneratedDocument[];
  media?: Media[];
  version?: number;
}

export interface PaginationLink {
  url: string | null;
  label: string;
  active: boolean;
}

export interface Meta {
  current_page: number;
  from: number;
  last_page: number;
  links: PaginationLink[];
  path: string;
  per_page: number;
  to: number;
  total: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta: Meta;
}

export interface GetTemplatesParams {
  relationships?: string;
  per_page?: number;
  page?: number;
  "filter[display_name]"?: string;
  "filter[mapping_name]"?: string;
  "filter[status]"?: string;
  "filter[search]"?: string;
}

export interface GetTemplateByIdParams {
  relationships?: string;
}

/**
 * Hook para obtener la lista de templates de documentos
 *
 * @param params - Parámetros de consulta opcionales
 * @returns Query result con la lista paginada de templates
 */
export const useGetDocumentTemplates = (params?: GetTemplatesParams) => {
  return useQuery({
    queryKey: ["document-templates", params],
    queryFn: async (): Promise<PaginatedResponse<DocumentTemplate>> => {
      const response = await axios.get(`/admin/v1/generator-template`, {
        params,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: 2,
  });
};

export const useGetDocumentTemplatesAsInifinite = (
  params?: Omit<GetTemplatesParams, "page">,
) => {
  return useInfiniteQuery({
    queryKey: ["document-templates-infinite", params],
    queryFn: async ({
      pageParam = 1,
    }): Promise<PaginatedResponse<DocumentTemplate>> => {
      const response = await axios.get(`/admin/v1/generator-template`, {
        params: {
          ...params,
          page: pageParam,
        },
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = (
        lastPage as PaginatedResponse<DocumentTemplate>
      ).meta;
      if (current_page < last_page) return current_page + 1;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook para obtener un template específico por ID
 *
 * @param templateId - ID del template a obtener
 * @param params - Parámetros de consulta opcionales
 * @returns Query result con los datos del template
 */
export const useGetDocumentTemplateById = (
  templateId?: string,
  params?: GetTemplateByIdParams,
) => {
  return useQuery({
    queryKey: ["document-template", templateId, params],
    queryFn: async (): Promise<{ data: DocumentTemplate }> => {
      const response = await axios.get(
        `/admin/v1/generator-template/${templateId}`,
        {
          params,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );
      return response.data;
    },
    enabled: !!templateId, // Solo ejecutar si se proporciona un ID
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: 2,
  });
};

/**
 * Hook para obtener un PDF binario
 *
 * @param templateId - ID del template a obtener
 * @param params - Parámetros
 * @returns Query result con los datos del template
 */

export const useGeneratePdfPreview = () => {
  return useMutation({
    mutationFn: async ({
      templateId,
      variables,
    }: {
      templateId?: string;
      variables?: { variables: Record<string, string> };
    }) => {
      const response = await axios.post(
        `/admin/v1/generator-template/${templateId}/preview`,
        variables,
        {
          responseType: "blob",
          headers: {
            Accept: "application/pdf",
          },
        },
      );

      const file = new Blob([response.data], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, "_blank");

      setTimeout(() => URL.revokeObjectURL(fileURL), 10000);
    },
  });
};

interface GeneratedDocumentMedia {
  uuid: string;
  model_type: string;
  model_id: string;
  collection_name: string;
  name: string;
  file_name: string;
  mime_type: string;
  disk: string;
  size: number;
  custom_properties: {
    template_id: string;
    template_name: string;
    generated_at: string;
    variables_count: number;
  };
  original_url: string;
  preview_url: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

interface GeneratedDocumentResponse {
  id: string;
  media: GeneratedDocumentMedia[];
}

/**
 * Hook para obtener un documento generado por su ID
 *
 * @param documentId - ID del documento generado
 * @returns Query result con los datos del documento
 */
export const useGetGeneratedDocument = (documentId: string) => {
  return useQuery({
    queryKey: ["generated-document", documentId],
    queryFn: async (): Promise<GeneratedDocumentResponse> => {
      const response = await axios.get(
        `/admin/v1/generated-document/${documentId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );
      return response.data.data;
    },
    enabled: !!documentId,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
};

/**
 * Hook para obtener la URL de descarga de un documento generado
 *
 * @returns Mutation para obtener URL y abrir en nueva ventana
 */
export const useDownloadGeneratedDocument = () => {
  return useMutation({
    mutationFn: async (documentId: string) => {
      const response = await axios.get(
        `/admin/v1/generated-document/${documentId}/download`,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      window.open(data.url || data, "_blank");
    },
  });
};
